import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { useRequireAuth } from '@/hooks/use-require-auth';
import { declineEventAttendance, getEvent } from '@/lib/events-api';
import { ArrowLeft, Lock, CheckCircle, XCircle, Circle, Clock, Shield } from 'lucide-react';

function ChecklistItem({ label, done, tone = 'success', testID }) {
  const color = done ? '#1F8A55' : tone === 'danger' ? '#C93434' : '#5B6472';
  const Icon = done ? CheckCircle : tone === 'danger' ? XCircle : Circle;
  return (
    <div className="flex items-center gap-3 py-2" data-testid={testID}>
      <Icon className="w-5 h-5" style={{ color }} />
      <span className={`text-sm font-semibold ${done ? 'text-[#0B1B46]' : ''}`} style={{ color: done ? '#0B1B46' : color }}>{label}</span>
    </div>
  );
}

export default function HostApprovalPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { ready } = useRequireAuth();
  const [sp] = useSearchParams();
  const eventId = sp.get('event_id') || '';

  const [event, setEvent] = useState(null);
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!ready || !eventId) return;
    (async () => {
      setLoading(true);
      try { const b = await getEvent(eventId); setEvent(b.event); setMembership(b.membership); }
      finally { setLoading(false); }
    })();
  }, [ready, eventId]);

  async function onRequestApproval() {
    setError(null); setBusy(true);
    try { const b = await declineEventAttendance(eventId, 'Location declined — requesting host approval'); setMembership(b.membership); }
    catch (e) { setError(e.message || "Couldn't submit request."); }
    finally { setBusy(false); }
  }

  const status = membership?.status || 'REGISTERED';
  const alreadyPending = status === 'HOST_PENDING';
  const approved = status === 'HOST_APPROVED' || status === 'GEO_VERIFIED';

  return (
    <div className="max-w-lg mx-auto px-4 py-6" data-testid="host-approval-required-screen">
      <button onClick={() => navigate('/contrib-event', { replace: true })} className="flex items-center gap-1 text-[#146BFF] font-semibold text-sm mb-4 hover:underline" data-testid="approval-back-btn">
        <ArrowLeft className="w-4 h-4" /> back
      </button>

      <div className="flex justify-center mb-4">
        <div className="w-20 h-20 rounded-full bg-[#FFF6E5] flex items-center justify-center">
          <Lock className="w-10 h-10 text-[#B86500]" />
        </div>
      </div>
      <h1 className="text-2xl font-extrabold text-[#0B1B46] text-center mb-1" data-testid="approval-title">Host Approval Required</h1>
      <p className="text-sm text-[#5B6472] text-center mb-4" data-testid="approval-explainer">
        You've joined the event, but the host needs to approve your contributor access before you can upload.
      </p>

      {loading ? (
        <div className="flex justify-center py-4" data-testid="approval-loading"><div className="w-6 h-6 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" /></div>
      ) : event ? (
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 mb-4" data-testid="approval-event-card">
          <p className="text-lg font-extrabold text-[#0B1B46]">{event.name}</p>
          <span className="inline-block mt-1 px-2.5 py-0.5 bg-[#EFF5FF] text-[#146BFF] text-xs font-bold rounded-full" data-testid="approval-joined-badge">You've joined</span>
        </div>
      ) : null}

      <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 mb-4" data-testid="approval-checklist-card">
        <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest mb-2 uppercase">Your verification progress</p>
        <ChecklistItem label="Email Verified" done={!!user?.email_verified} testID="checklist-email" />
        <ChecklistItem label="Phone Verified" done={!!user?.phone_verified} testID="checklist-phone" />
        <ChecklistItem label="Event Joined" done={!!membership} testID="checklist-joined" />
        <ChecklistItem label="Geolocation: Declined" done={false} tone="danger" testID="checklist-geo-declined" />
      </div>

      {approved ? (
        <div className="bg-[#E4F4EC] border border-[#1F8A55]/20 rounded-xl p-4 mb-4" data-testid="approval-already-approved">
          <div className="flex items-center gap-2 mb-1"><CheckCircle className="w-5 h-5 text-[#1F8A55]" /><span className="font-bold text-[#0B1B46]">You're approved</span></div>
          <p className="text-sm text-[#5B6472]">{status === 'GEO_VERIFIED' ? 'Your attendance was geo-verified.' : 'The host has manually approved your access.'}</p>
          <button onClick={() => navigate('/contrib-event', { replace: true })}
            className="w-full h-10 mt-3 bg-[#146BFF] text-white font-semibold rounded-lg hover:bg-[#0F55CC] transition-colors"
            data-testid="approval-continue-btn">Continue to Event</button>
        </div>
      ) : alreadyPending ? (
        <div className="bg-[#FBEEDA] border border-[#B86500]/20 rounded-xl p-4 mb-4" data-testid="approval-pending-card">
          <div className="flex items-center gap-2 mb-1"><Clock className="w-5 h-5 text-[#B86500]" /><span className="font-bold text-[#0B1B46]">Approval requested</span></div>
          <p className="text-sm text-[#5B6472]">The host has been notified. You'll be able to upload once they approve.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2 mb-4">
          <button onClick={onRequestApproval} disabled={busy}
            className="w-full h-10 bg-[#146BFF] hover:bg-[#0F55CC] disabled:opacity-50 text-white font-semibold rounded-lg transition-colors"
            data-testid="approval-request-btn">{busy ? 'Requesting…' : 'Request Host Approval'}</button>
          <button onClick={() => navigate(`/verify-attendance?event_id=${eventId}`, { replace: true })}
            className="w-full h-10 bg-white border border-[#E3E8EF] text-[#0B1B46] font-semibold rounded-lg hover:bg-[#F3F5F8] transition-colors"
            data-testid="approval-enable-location-btn">Enable Location Instead</button>
        </div>
      )}

      {error && <div className="bg-[#FBE5E5] border border-[#C93434]/20 rounded-lg p-3 text-sm text-[#C93434] mb-4" data-testid="approval-error">{error}</div>}

      <div className="bg-[#EFF5FF] border border-[#D6E4FF] rounded-xl p-3 flex items-start gap-2" data-testid="approval-shield-callout">
        <Shield className="w-4 h-4 text-[#146BFF] mt-0.5 flex-shrink-0" />
        <p className="text-sm text-[#5B6472]">Verified identity and event membership keep your submissions private. Host approval grants upload rights without revealing your live location.</p>
      </div>
    </div>
  );
}
